import React, { useState, useMemo } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X } from 'lucide-react';

// Parse a value into a local Date. Accepts 'YYYY-MM-DD' strings, Date objects,
// timestamps/ISO strings, or anything Date can parse. Returns null when invalid.
const parseValueToDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === 'string') {
        // Parse YYYY-MM-DD as local date (not UTC)
        const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (match) {
            const [, y, m, d] = match;
            return new Date(Number(y), Number(m) - 1, Number(d));
        }
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
};

const PremiumDatePicker = ({ value, onChange, label, align = 'left' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [viewMode, setViewMode] = useState('calendar'); // 'calendar', 'months', 'years'
    const [viewDate, setViewDate] = useState(() => parseValueToDate(value) || new Date());

    const monthNames = [
        "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
        "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
    ];

    const formatDate = (date) => {
        const d = new Date(date);
        let month = '' + (d.getMonth() + 1);
        let day = '' + d.getDate();
        const year = d.getFullYear();
        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;
        return [year, month, day].join('-');
    };

    const calendarGrid = useMemo(() => {
        const year = viewDate.getFullYear();
        const month = viewDate.getMonth();

        // First day of current month (0-6, 0=Sun, but let's use 1=Mon for TR style)
        // JS getDay(): 0=Sun, 1=Mon...
        let firstDay = new Date(year, month, 1).getDay();
        // Adjust for Monday start: (d + 6) % 7
        const firstDayAdjusted = (firstDay + 6) % 7;

        const days = [];

        // Prev month days
        const prevMonthLastDay = new Date(year, month, 0).getDate();
        for (let i = firstDayAdjusted; i > 0; i--) {
            days.push({
                day: prevMonthLastDay - i + 1,
                month: month - 1,
                year,
                isCurrentMonth: false
            });
        }

        // Current month days
        const currentMonthLastDay = new Date(year, month + 1, 0).getDate();
        for (let i = 1; i <= currentMonthLastDay; i++) {
            days.push({
                day: i,
                month,
                year,
                isCurrentMonth: true
            });
        }

        // Next month days to fill up to exactly 42 cells (6 rows)
        const remaining = 42 - days.length;
        for (let i = 1; i <= remaining; i++) {
            days.push({
                day: i,
                month: month + 1,
                year,
                isCurrentMonth: false
            });
        }

        return days;
    }, [viewDate]);

    const handleDateSelect = (dObj) => {
        const selectedDate = new Date(dObj.year, dObj.month, dObj.day);
        onChange(formatDate(selectedDate));
        setIsOpen(false);
    };

    const nextMonth = (e) => {
        e.stopPropagation();
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
    };

    const prevMonth = (e) => {
        e.stopPropagation();
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
    };

    const todayStr = formatDate(new Date());
    const selectedDate = parseValueToDate(value);
    const selectedStr = selectedDate ? formatDate(selectedDate) : null;

    const years = [];
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 100; i <= currentYear + 10; i++) {
        years.push(i);
    }
    // Reverse years to show newest first for easier selection
    years.reverse();

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            {label && <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>{label}</label>}
            <div
                onClick={() => {
                    setIsOpen(!isOpen);
                    setViewMode('calendar');
                }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 14px',
                    background: 'var(--bg-sidebar)',
                    border: '1px solid var(--border)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    color: value ? 'var(--text-main)' : 'var(--text-muted)',
                    transition: 'border-color 0.2s'
                }}
            >
                <CalendarIcon size={18} color="var(--primary)" />
                <span style={{ fontWeight: 500 }}>{(() => {
                    const parsed = parseValueToDate(value);
                    return parsed
                        ? parsed.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })
                        : 'Tarih Seçin...';
                })()}</span>
            </div>

            {isOpen && (
                <div
                    className="premium-calendar-popup"
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 10px)',
                        ...(align === 'right' ? { right: 0 } : { left: 0 }),
                        background: 'var(--bg-sidebar)',
                        border: '1px solid var(--border)',
                        borderRadius: '20px',
                        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
                        padding: '1.25rem',
                        zIndex: 3000,
                        width: '320px',
                        maxWidth: '90vw',
                        animation: 'dateModalIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)'
                    }}
                >
                    <style>{`
                        @keyframes dateModalIn {
                            from { transform: translateY(-10px); opacity: 0; }
                            to { transform: translateY(0); opacity: 1; }
                        }
                        @media (max-width: 480px) {
                            .premium-calendar-popup {
                                position: fixed !important;
                                top: 50% !important;
                                left: 50% !important;
                                bottom: auto !important;
                                right: auto !important;
                                transform: translate(-50%, -50%) !important;
                                width: 280px !important;
                                max-width: 85vw !important;
                                margin: 0 !important;
                                box-shadow: 0 20px 60px rgba(0,0,0,0.5) !important;
                                animation: dateModalCenter 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
                            }
                        }
                        @keyframes dateModalCenter {
                            from { transform: translate(-50%, -45%); opacity: 0; }
                            to { transform: translate(-50%, -50%); opacity: 1; }
                        }
                    `}</style>

                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <button
                                type="button"
                                onClick={() => setViewMode(viewMode === 'months' ? 'calendar' : 'months')}
                                style={{ background: 'var(--bg-main)', border: 'none', padding: '4px 8px', borderRadius: '6px', fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-main)', cursor: 'pointer' }}
                            >
                                {monthNames[viewDate.getMonth()]}
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMode(viewMode === 'years' ? 'calendar' : 'years')}
                                style={{ background: 'var(--bg-main)', border: 'none', padding: '4px 8px', borderRadius: '6px', fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-main)', cursor: 'pointer' }}
                            >
                                {viewDate.getFullYear()}
                            </button>
                        </div>
                        <div style={{ display: 'flex', gap: '4px' }}>
                            <button type="button" onClick={prevMonth} style={{ background: 'transparent', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-main)' }}><ChevronLeft size={18} /></button>
                            <button type="button" onClick={nextMonth} style={{ background: 'transparent', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-main)' }}><ChevronRight size={18} /></button>
                        </div>
                    </div>

                    {viewMode === 'calendar' && (
                        <>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '8px' }}>
                                {['Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct', 'Pz'].map(d => (
                                    <div key={d} style={{ textAlign: 'center', fontSize: '0.65rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{d}</div>
                                ))}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', minHeight: '240px' }}>
                                {calendarGrid.map((dObj, idx) => {
                                    const currentStr = formatDate(new Date(dObj.year, dObj.month, dObj.day));
                                    const isToday = currentStr === todayStr;
                                    const isSelected = currentStr === selectedStr;

                                    return (
                                        <div
                                            key={idx}
                                            onClick={() => handleDateSelect(dObj)}
                                            style={{
                                                aspectRatio: '1',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                borderRadius: '10px',
                                                fontSize: '0.875rem',
                                                transition: 'all 0.2s',
                                                background: isSelected ? 'var(--primary)' : isToday ? 'rgba(var(--primary-rgb), 0.1)' : 'transparent',
                                                color: isSelected ? 'white' : dObj.isCurrentMonth ? 'var(--text-main)' : 'var(--text-muted)',
                                                fontWeight: isSelected || isToday ? '700' : '400',
                                                opacity: dObj.isCurrentMonth ? 1 : 0.4
                                            }}
                                        >
                                            {dObj.day}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {viewMode === 'months' && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', minHeight: '240px' }}>
                            {monthNames.map((m, idx) => (
                                <div
                                    key={m}
                                    onClick={() => {
                                        setViewDate(new Date(viewDate.getFullYear(), idx, 1));
                                        setViewMode('calendar');
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '12px 4px',
                                        borderRadius: '10px',
                                        fontSize: '0.8125rem',
                                        fontWeight: viewDate.getMonth() === idx ? '700' : '400',
                                        background: viewDate.getMonth() === idx ? 'var(--primary)' : 'var(--bg-main)',
                                        color: viewDate.getMonth() === idx ? 'white' : 'var(--text-main)',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {m}
                                </div>
                            ))}
                        </div>
                    )}

                    {viewMode === 'years' && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', minHeight: '240px', overflowY: 'auto' }}>
                            {years.map(y => (
                                <div
                                    key={y}
                                    onClick={() => {
                                        setViewDate(new Date(y, viewDate.getMonth(), 1));
                                        setViewMode('calendar');
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '12px 4px',
                                        borderRadius: '10px',
                                        fontSize: '0.8125rem',
                                        fontWeight: viewDate.getFullYear() === y ? '700' : '400',
                                        background: viewDate.getFullYear() === y ? 'var(--primary)' : 'var(--bg-main)',
                                        color: viewDate.getFullYear() === y ? 'white' : 'var(--text-main)',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {y}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {isOpen && (
                <div
                    onClick={() => setIsOpen(false)}
                    style={{ position: 'fixed', inset: 0, zIndex: 2999 }}
                ></div>
            )}
        </div>
    );
};

export default PremiumDatePicker;
